import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';

// Simple in-memory cache to avoid redundant fetches for the same group
const memberCache = new Map<string, { count: number; ids: string[]; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const useGroupMemberCount = (groupId: string, initialCount?: number) => {
    const [data, setData] = useState<{ count: number; ids: string[] }>({
        count: initialCount || 0,
        ids: []
    });

    useEffect(() => {
        if (!groupId) return;

        // Check cache first
        const cached = memberCache.get(groupId);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
            setData({ count: cached.count, ids: cached.ids });
            return;
        }

        let isCancelled = false;

        // One-time fetch instead of real-time listener to avoid memory bloat
        const fetchMembers = async () => {
            try {
                const playersRef = collection(db, 'groups', groupId, 'players');
                const snapshot = await getDocs(playersRef);
                
                if (isCancelled) return;

                const result = {
                    count: snapshot.size,
                    ids: snapshot.docs.map(doc => doc.id)
                };

                // Update cache
                memberCache.set(groupId, { ...result, timestamp: Date.now() });
                setData(result);
            } catch (error) {
                console.error("Error fetching group member count:", error);
            }
        };

        fetchMembers();

        return () => { isCancelled = true; };
    }, [groupId]);

    return data;
};
